import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import ExerciseWeight from "../ExerciseWeight";
import { useQuery } from "@tanstack/react-query";
import { lazy, Suspense, useState } from "react";
import { Theme } from "../../../data/style/Theme";
import { useAuth } from "../../../context/AuthContext";
import { getWorkoutById } from "../../../api/workouts";
import useScreenSize from "../../../context/ScreenContext";
import { LEVELOPTIONS } from "../../../data/assets/arrays";
import {
  CenteredRow,
  SpacedRow,
  MainText,
  MainButton,
  SmallTitle,
  AllCenteredColumn,
  FormButton,
} from "../../../data/style/Style";

const ScoreModal = lazy(() => import("../../Modals/ScoreModal"));
const MILE_CONVERT = 0.000621371192;

const WeeklyWorkout = (props) => {
  let { wodId, level, setLevel } = props;
  const { profile } = useAuth();
  const { mobileDevice } = useScreenSize();
  const [scoreModalActive, setScoreModalActive] = useState(false);

  const { data, isPending } = useQuery({
    queryKey: ["workout", parseInt(wodId)],
    queryFn: ({ signal }) => getWorkoutById({ signal, wodId }),
    staleTime: 60 * 24 * 1000,
  });

  const levelColor =
    !isPending &&
    LEVELOPTIONS.find((item) => item.id === data.level_info.level).color;
  return (
    <>
      <LevelPils setLevel={setLevel} level={level} />
      {!isPending && (
        <>
          <Stack width={"100%"} alignItems={"center"} gap={1}>
            <MainText
              sx={{
                fontSize: mobileDevice ? "0.7rem" : "0.9rem",
                color: levelColor + " !important",
                width: "100%",
              }}
            >
              {data.type !== 4 ? (
                <>
                  {data.type_name} · {data.timecap_min} minutes
                </>
              ) : (
                <>
                  {data.type_name} ·{" "}
                  {data.timecap === 0
                    ? `${data.rounds} rounds: ${
                        data.every
                      } minutes on / ${data.rest} ${
                        data.rest < 2 ? "minute" : "minutes"
                      } off`
                    : `Every ${data.every} minutes for ${data.timecap_min} minutes`}
                </>
              )}
            </MainText>
            <Stack
              alignItems={"center"}
              minWidth={mobileDevice ? "95%" : "70%"}
            >
              {data.structure_display && data.type === 1 && (
                <Box textAlign={"center"} mb={2}>
                  <MainText
                    fontSize={mobileDevice ? "1rem" : "1.2rem"}
                    fontWeight={700}
                    sx={{
                      color: levelColor + " !important",
                    }}
                  >
                    {data.structure_name}
                  </MainText>
                </Box>
              )}
              <Stack minHeight={"80px"} gap={1} width={"100%"}>
                {data.exercises.map((item, index) => (
                  <WorkoutExercise
                    key={index}
                    exercise={item}
                    workoutLevel={data.level_info.level}
                    structureDisplay={data.structure_display}
                  />
                ))}
              </Stack>
            </Stack>
            <Suspense>
              <ScoreModal
                scoreModalActive={scoreModalActive}
                setScoreModalActive={setScoreModalActive}
                wodId={wodId}
                isWeekly={true}
              />
            </Suspense>
          </Stack>
        </>
      )}
      {!isPending && profile.level === data.level_info.level && (
        <>
          {profile.wow_place === 0 ? (
            <MainButton
              sx={{ marginTop: 2 }}
              onClick={() => setScoreModalActive(true)}
            >
              <MainText fontWeight={600} fontSize={"0.9rem"}>
                Submit your score
              </MainText>
            </MainButton>
          ) : (
            <SpacedRow
              alignItems={"center"}
              width={"95%"}
              p={1}
              sx={{
                border: 1,
                borderColor: Theme.palette.border,
                borderRadius: 4,
              }}
              mt={1}
              gap={1}
            >
              <Stack gap={1}>
                <SmallTitle fontSize={"0.8rem"}>✅ Your score</SmallTitle>
                <CenteredRow ml={2} gap={1}>
                  <MainText
                    fontWeight={700}
                    sx={{ color: Theme.palette.orange + " !important" }}
                  >
                    {profile?.wow_score}
                  </MainText>
                  <AllCenteredColumn
                    p={0.5}
                    sx={{
                      background: Theme.palette.gold + "22",
                      border: 1,
                      borderRadius: 2,
                      color: Theme.palette.gold,
                    }}
                  >
                    <MainText
                      sx={{ color: Theme.palette.gold + " !important" }}
                      fontSize={"0.6rem"}
                    >
                      #{profile.wow_place}
                    </MainText>
                  </AllCenteredColumn>
                </CenteredRow>
              </Stack>
              <Box>
                <FormButton
                  onClick={() => setScoreModalActive(true)}
                  alt={true}
                  small={true}
                >
                  Update
                </FormButton>
              </Box>
            </SpacedRow>
          )}
        </>
      )}
    </>
  );
};

export default WeeklyWorkout;

const WorkoutExercise = ({ exercise, workoutLevel, structureDisplay }) => {
  const { profile, settings } = useAuth();
  const { mobileDevice } = useScreenSize();

  return (
    <CenteredRow gap={2}>
      <Box minWidth={"23px"}>
        <MainText
          fontWeight={700}
          fontSize={mobileDevice ? "1rem" : "1.2rem"}
          sx={{
            color:
              LEVELOPTIONS.find((item) => item.id === workoutLevel).color +
              " !important",
          }}
        >
          {parseInt(exercise.reps) === 5000 ? (
            "max"
          ) : (
            <>
              {parseInt(exercise.reps) !== 0 && !structureDisplay && (
                <>
                  {profile.gender === 0
                    ? parseInt(exercise.reps)
                    : parseInt(exercise.female_reps)}
                </>
              )}
            </>
          )}
        </MainText>
      </Box>
      <SpacedRow flex={1} alignItems={"center"} gap={3}>
        <MainText fontSize={mobileDevice ? "0.8rem" : "1rem"}>
          {exercise.units !== "rep" && <>{exercise.units}&nbsp;</>}
          {exercise.exercise_name}
        </MainText>
        {exercise.exercise_name === "run" && exercise.alt_reps > 0 && (
          <MainText fontSize={"0.8rem"}>
            (
            {settings.distance === 0
              ? exercise.alt_reps + " m"
              : (exercise.alt_reps * MILE_CONVERT).toFixed(2) + " mi"}
            )
          </MainText>
        )}
        {exercise.category >= 4 && (
          <ExerciseWeight weight_info={exercise.weight_info} />
        )}
      </SpacedRow>
    </CenteredRow>
  );
};

const LevelPils = ({ level, setLevel }) => {
  const { mobileDevice } = useScreenSize();
  return (
    <Stack mb={2} direction={"row"} width={"100%"} gap={mobileDevice ? 1 : 2}>
      {LEVELOPTIONS.map((item) => (
        <Box
          key={item.id}
          onClick={() => setLevel(item.id)}
          sx={{
            border: 1,
            borderRadius: 4,
            padding: "3px 12px",
            borderColor: item.id === level ? item.color : Theme.palette.border,
            cursor: "pointer",
          }}
        >
          <MainText
            sx={{
              color:
                item.id === level
                  ? item.color + " !important"
                  : Theme.palette.border + " !important",
              fontSize: mobileDevice ? "0.6rem" : "0.8rem",
            }}
          >
            {item.name}
          </MainText>
        </Box>
      ))}
    </Stack>
  );
};
